import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { CANONICAL_BASE, SEO_ROUTES, PRIVATE_PREFIXES, INDEXABLE_PROJECT_IDS, isKnownRoute } from './seoConfig';
import { BLOG_SEO } from '@/data/blogPosts';

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || 'meta');
    Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
};

// An unknown URL must not advertise a canonical pointing at itself, and the
// static index.html ships one, so it has to be taken back out rather than
// merely left unset. Only tags this component also re-creates may be removed,
// otherwise visiting a 404 would strip them for the rest of the session.
const removeMeta = (selector) => {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
};

export const NOT_FOUND_SEO = {
  title: {
    en: '404 — Page Not Found | Nexus Design AI',
    tr: '404 — Sayfa Bulunamadı | Nexus Design AI',
  },
  description: {
    en: "The page you're looking for doesn't exist or has been moved.",
    tr: 'Aradığınız sayfa mevcut değil veya taşınmış olabilir.',
  },
};

const blogMatchPrivate = (route) => {
  const m = route.match(/^\/blog\/([^/]+)$/);
  return Boolean(m && !BLOG_SEO[m[1]]);
};

export const SeoManager = () => {
  const { pathname } = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    const lang = language === 'tr' ? 'tr' : 'en';
    let route = pathname.replace(/\/+$/, '') || '/';
    if (route === '/privacy') route = '/privacy-policy';
    if (route === '/terms') route = '/terms-of-service';

    const isProjectDetail = /^\/projects\/[^/]+$/.test(route);
    const projectId = isProjectDetail ? route.split('/')[2] : null;
    const isPrivate = PRIVATE_PREFIXES.some((p) => route === p.replace(/\/$/, '') || route.startsWith(p))
      || (isProjectDetail && !INDEXABLE_PROJECT_IDS.includes(projectId))
      || (blogMatchPrivate(route));

    let cfg = SEO_ROUTES[route];
    const blogMatch = route.match(/^\/blog\/([^/]+)$/);
    if (!cfg && blogMatch && BLOG_SEO[blogMatch[1]]) {
      cfg = BLOG_SEO[blogMatch[1]];
    }
    if (!cfg && isProjectDetail && INDEXABLE_PROJECT_IDS.includes(projectId)) {
      cfg = {
        title: {
          en: 'Public Demo Project — Nexus Design AI',
          tr: 'Herkese Açık Demo Proje — Nexus Design AI',
        },
        description: {
          en: 'A public demo of an AI-generated engineering package. Sensitive engineering data stays protected behind sign-in.',
          tr: 'AI üretimi mühendislik paketinin herkese açık demosu. Hassas mühendislik verileri giriş korumasındadır.',
        },
      };
    }
    // A URL that matches no route in App.js lands on NotFoundPage. Emitting the
    // homepage config for it is what made every bogus URL a soft 404: duplicate
    // homepage metadata under `index, follow` with a self-referencing canonical.
    // Note this is keyed on the route table, not on SEO_ROUTES — real pages such
    // as /dashboard deliberately carry no marketing metadata.
    const isNotFound = !isKnownRoute(route);
    if (isNotFound) cfg = NOT_FOUND_SEO;
    // A real route with no marketing metadata of its own (/dashboard, /studio).
    if (!cfg) cfg = SEO_ROUTES['/'];

    const title = cfg.title[lang] || cfg.title.en;
    const description = cfg.description[lang] || cfg.description.en;
    const canonicalUrl = `${CANONICAL_BASE}${route === '/' ? '/' : route}`;

    document.title = title;

    if (isNotFound) {
      upsertMeta('meta[name="description"]', { create: { name: 'description' }, set: { content: description } });
      upsertMeta('meta[property="og:title"]', { create: { property: 'og:title' }, set: { content: title } });
      upsertMeta('meta[property="og:description"]', { create: { property: 'og:description' }, set: { content: description } });
      upsertMeta('meta[name="twitter:title"]', { create: { name: 'twitter:title' }, set: { content: title } });
      upsertMeta('meta[name="twitter:description"]', { create: { name: 'twitter:description' }, set: { content: description } });
      upsertMeta('meta[name="robots"]', { create: { name: 'robots' }, set: { content: 'noindex, nofollow' } });
      upsertMeta('meta[name="googlebot"]', { create: { name: 'googlebot' }, set: { content: 'noindex, nofollow' } });
      removeMeta('link[rel="canonical"]');
      removeMeta('meta[property="og:url"]');
      return;
    }

    upsertMeta('meta[property="og:locale"]', { create: { property: 'og:locale' }, set: { content: lang === 'tr' ? 'tr_TR' : 'en_US' } });
    upsertMeta('meta[name="language"]', { create: { name: 'language' }, set: { content: lang === 'tr' ? 'Turkish' : 'English' } });
    upsertMeta('meta[name="description"]', { create: { name: 'description' }, set: { content: description } });
    upsertMeta('link[rel="canonical"]', { tag: 'link', create: { rel: 'canonical' }, set: { href: canonicalUrl } });
    upsertMeta('meta[property="og:title"]', { create: { property: 'og:title' }, set: { content: title } });
    upsertMeta('meta[property="og:description"]', { create: { property: 'og:description' }, set: { content: description } });
    upsertMeta('meta[property="og:url"]', { create: { property: 'og:url' }, set: { content: canonicalUrl } });
    upsertMeta('meta[name="twitter:title"]', { create: { name: 'twitter:title' }, set: { content: title } });
    upsertMeta('meta[name="twitter:description"]', { create: { name: 'twitter:description' }, set: { content: description } });
    upsertMeta('meta[name="robots"]', {
      create: { name: 'robots' },
      set: { content: isPrivate ? 'noindex, nofollow' : 'index, follow' },
    });
    upsertMeta('meta[name="googlebot"]', {
      create: { name: 'googlebot' },
      set: { content: isPrivate ? 'noindex, nofollow' : 'index, follow' },
    });
  }, [pathname, language]);

  return null;
};
